<template>
  <section
    id="programs"
    class="program-selection"
    aria-labelledby="program-selection-title"
    :dir="direction"
  >
    <div class="selection-inner">
      <header class="selection-heading">
        <p class="selection-eyebrow">Idmanify</p>
        <h2 id="program-selection-title">
          {{ t('conversion.selectionTitle') }}
        </h2>
        <p>{{ t('conversion.selectionIntro') }}</p>
      </header>
      <div class="program-finder">
        <div>
          <h3>{{ t('conversion.finderTitle') }}</h3>
          <p>{{ t('conversion.finderIntro') }}</p>
        </div>
        <NuxtLink
          :to="localePath('/product-suggestion')"
          prefetch-on="interaction"
          class="finder-link"
          data-cy="program-finder"
          >{{ t('conversion.finderLink') }} <EditorialArrow
        /></NuxtLink>
      </div>
      <div v-if="items.length" class="selection-grid">
        <article
          v-for="item in items"
          :key="item.id || item.slug"
          class="selection-card"
          :data-program-id="item.id"
        >
          <img
            v-if="item.picture"
            :src="item.picture"
            :alt="item.title"
            width="640"
            height="640"
            loading="lazy"
            decoding="async"
            class="selection-image"
          />
          <div class="selection-body">
            <p v-if="programAudience(item.id)" class="selection-audience">
              {{ t(`conversion.${programAudience(item.id)}`) }}
            </p>
            <h3>{{ item.title }}</h3>
            <ul class="selection-benefits">
              <li
                v-for="(benefit, index) in benefits(item).slice(0, 3)"
                :key="index"
              >
                {{ benefit }}
              </li>
            </ul>
            <div class="selection-actions">
              <details v-if="benefits(item).length > 3" class="selection-more">
                <summary>{{ t('conversion.allBenefits') }}</summary>
                <ul>
                  <li
                    v-for="(benefit, index) in benefits(item).slice(3)"
                    :key="index"
                  >
                    {{ benefit }}
                  </li>
                </ul>
              </details>
              <NuxtLink
                :to="localePath(`/program/${item.slug}`)"
                class="selection-link"
                prefetch-on="interaction"
                :aria-label="`${t('conversion.details')}: ${item.title}`"
                @click="track('select_item', { itemId: item.id })"
              >
                {{ t('conversion.details') }} <EditorialArrow />
              </NuxtLink>
            </div>
          </div>
        </article>
      </div>
      <p v-else role="status" class="selection-empty">
        {{ t('conversion.empty') }}
        <NuxtLink :to="localePath({ name: 'contact' })">{{
          t('conversion.contact')
        }}</NuxtLink>
      </p>
    </div>
  </section>
</template>

<script setup>
import { programAudience, selectablePrograms } from '~/utils/conversion';
const props = defineProps({ programs: { type: Array, default: () => [] } });
const { t, locale } = useI18n();
const localePath = useLocalePath();
const { track } = useConversionTracking();
const items = computed(() => selectablePrograms(props.programs));
const direction = computed(() =>
  ['ar', 'ur'].includes(locale.value) ? 'rtl' : 'ltr'
);
const benefits = (item) =>
  Array.isArray(item.keyDescriptions)
    ? item.keyDescriptions.filter(
        (value) => typeof value === 'string' && value.trim()
      )
    : [];
</script>

<style scoped>
.program-selection {
  background: #f6f5f2;
  color: #17191d;
  padding: 4rem 1.5rem;
  scroll-margin-top: 6rem;
}
.selection-inner {
  max-width: 76rem;
  margin-inline: auto;
}
.selection-heading {
  max-width: 46rem;
  margin-block-end: 2rem;
}
.selection-eyebrow {
  color: #9a3412;
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}
.selection-heading h2 {
  font-size: clamp(1.75rem, 3vw, 2.6rem);
  font-weight: 800;
  line-height: 1.15;
  margin-block: 0.6rem 1rem;
}
.selection-heading > p:last-child {
  color: #4b5059;
  line-height: 1.65;
}
.program-finder {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 1rem 2rem;
  padding: 1.25rem;
  margin-block-end: 2rem;
  border: 1px solid #d7d9dc;
  border-radius: 0.75rem;
  background: #fff;
}
.program-finder h3 {
  font-size: 1rem;
  line-height: 1.5;
  font-weight: 750;
}
.program-finder p {
  font-size: 0.9rem;
  line-height: 1.6;
  color: #444b55;
  margin-block-start: 0.4rem;
}
.finder-link {
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  min-height: 44px;
  color: #9a3412;
  font-weight: 750;
  text-decoration: underline;
  text-underline-offset: 0.2rem;
}
.finder-link:focus-visible {
  outline: 3px solid #c2410c;
  outline-offset: 4px;
}
.selection-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem;
}
.selection-card {
  min-width: 0;
  background: #fff;
  border: 1px solid #d7d9dc;
  border-radius: 1rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.selection-image {
  width: 100%;
  aspect-ratio: 1;
  height: auto;
  object-fit: contain;
  background: #fafaf9;
}
.selection-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  overflow-wrap: anywhere;
}
.selection-audience {
  color: #9a3412;
  font-size: 0.85rem;
  font-weight: 800;
  margin-block-end: 0.65rem;
}
.selection-body h3 {
  font-size: 1.25rem;
  line-height: 1.4;
  font-weight: 750;
}
.selection-benefits,
.selection-more ul {
  list-style: disc;
  padding-inline-start: 1.2rem;
  margin-block: 1.15rem;
  color: #444b55;
  line-height: 1.6;
}
.selection-benefits li,
.selection-more li {
  margin-block: 0.5rem;
}
.selection-actions {
  margin-block-start: auto;
}
.selection-more {
  margin-block-end: 1rem;
  color: #444b55;
}
.selection-more summary {
  padding-block: 0.7rem;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 650;
  text-decoration: underline;
  text-underline-offset: 0.2rem;
}
.selection-link {
  padding: 0.9rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  min-height: 48px;
  background: #111827;
  color: #fff;
  font-weight: 750;
  border-radius: 0.6rem;
  text-decoration: none;
}
.selection-link:hover {
  background: #9a3412;
}
.selection-link:focus-visible,
summary:focus-visible {
  outline: 3px solid #c2410c;
  outline-offset: 4px;
}
.selection-empty {
  padding: 2rem 0;
  line-height: 1.6;
}
.selection-empty a {
  text-decoration: underline;
}
@media (min-width: 1200px) {
  .selection-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .selection-body {
    padding: 1.2rem;
  }
}
@media (max-width: 639px) {
  .selection-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .program-selection {
    padding: 2.5rem 1rem;
  }
}
</style>
